/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, ElementRef, OnInit, ViewChild  } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  FormActionPipelines,
  FormActions,
  FormFlowState,
} from '@gh/builder-shared';
import { Month, OfficeStatus } from '@gh/builder-shared-data';
import { ActionFlow, AppRouter, EnumDictionary, getDefaultEmail, getRouteInitData, TextDictionary } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { DEFAULT_EMAIL_SUFFIX } from '@gh/config';
import { DialogService, FormMode, getControlValue } from '@gh/core-ui';
import { assign } from 'lodash';
import { SubOfficeDetailsBusinessService } from '../../resources/sub-office-details-business.service';
import { SubOfficeDetails } from '../../resources/sub-office-details.model';
import { SubOfficeDetailsFormState } from './sub-office-details-form.service';
import { OfficeListItem } from '../../../offices/resources/office-list-item.model';
import { OfficeDetails } from '../../../offices/resources/office-details.model';

@Component({
  selector: 'gh-sub-office-details',
  template: require('./sub-office-details.component.html'),
  styles: [require('./sub-office-details.component.scss')],
  viewProviders: [SubOfficeDetailsFormState],
})
export class SubOfficeDetailsComponent extends AbstractFormComponent<SubOfficeDetails> implements OnInit {
  franchiseeOffices: OfficeListItem[];
  officeStatuses: EnumDictionary<OfficeStatus>;
  parentOffice: OfficeDetails;
  states: TextDictionary;
  months: EnumDictionary<Month>;
  emailSuffix = DEFAULT_EMAIL_SUFFIX;
  isModifyingOn: boolean = false;
  subOfficeId: ID;

  constructor(private route: ActivatedRoute,
              private router: AppRouter,
              private userContext: BuilderUserContext,
              private businessService: SubOfficeDetailsBusinessService,
              public form: SubOfficeDetailsFormState,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private formActions: FormActions,
              private dialogService: DialogService,
              private navigationService: BuilderNavigationService,
              ) {
    super();
  }

  @ViewChild('companyNameField') fieldToFocus!: ElementRef;

  get isOfficeView(): boolean {
    return this.userContext.officeView;
  }

  get title(): string {
    return getControlValue(this.form.group, 'displayName');
  }

  get email(): string {
    return getDefaultEmail(getControlValue(this.form.group, 'email'));
  }

  get pageTitle(): string {
    return {
      [FormMode.CreateMode]: 'lbl_sub_office_create',
      [FormMode.EditMode]: 'lbl_sub_office_edit',
      [FormMode.ViewMode]: '',
    }[this.form.mode];
  }

  get completeFlow(): ActionFlow<FormFlowState<SubOfficeDetails>> {
    return this.pipelines.complete({
      execute: this.commonActions.execute((state: FormFlowState<SubOfficeDetails>) => {
        if (this.form.createMode) {
          return this.businessService.create(state.form.submitValue);
        } else {
          return this.businessService.update(state.form.submitValue);
        }
      }),
      finish: (state) => {
        const { value, form } = state;
        this.isModifyingOn = false;
        if (this.form.createMode) {
          this.router.switchTo(['/setup/sub-offices', value.id]);
        } else {
          form.build(value);
        }
      },
    });
  }

  ngOnInit(): void {
    const { officeDetails, ...init } = getRouteInitData(this.route);

    assign(this, init);
    this.subOfficeId = officeDetails.id;
    this.form.build(officeDetails);
    if (this.parentOffice) {
      this.form.updateEmail(this.parentOffice?.email || '');
    }
  }

  ngAfterViewInit(): void {
    if(this.form.mode === 1)this.fieldToFocus.nativeElement.focus()
  }

  onApplyLicense(): void {
    this.form.updateLicenseState();
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
      entityId: entityId ? entityId : undefined,
      entityType: entityType ? entityType : 'SUB_OFFICE',
      label: label ? label : 'lbl_menu_sub_offices',
    });
  }

  onOfficeSelect({ value }: any): void {
    const targetOffice = this.franchiseeOffices.find((office) => office.officeId === value);
    this.form.updateEmail(targetOffice?.email || '');
  }
}
